import { canonicalJson } from './json'
import type { ActionControl, ActionOption, GameCommand } from './types'

export type SetControl = Extract<ActionControl, { kind: 'set' }>

export function setControl(action: ActionOption): SetControl | undefined {
  return action.control?.kind === 'set' ? action.control : undefined
}

/** A structurally valid set control; hosts reject anything else before it reaches a player. */
export function validSetControl(control: SetControl): boolean {
  const values = control.options.map(option => option.value)
  return Number.isSafeInteger(control.min) && Number.isSafeInteger(control.max) && control.min >= 0 && control.max >= control.min
    && control.max <= values.length && new Set(values).size === values.length
    && values.every(value => typeof value === 'string' && value.length > 0)
    && (control.key === undefined || (typeof control.key === 'string' && control.key.length > 0))
}

/** Components that toggle a value of a set-controlled action. */
export function selectionTargets(action: ActionOption): readonly string[] {
  return setControl(action)?.options.flatMap(option => option.targetId ? [option.targetId] : []) ?? []
}

/** The exact command for a chosen subset, or undefined when the subset is not currently legal. */
export function selectionCommand(action: ActionOption, values: readonly string[]): GameCommand | undefined {
  const control = setControl(action)
  if (!control || !validSetControl(control)) return undefined
  const order = control.options.map(option => option.value)
  if (values.length < control.min || values.length > control.max || new Set(values).size !== values.length
    || values.some(value => !order.includes(value))) return undefined
  const ordered = order.filter(value => values.includes(value))
  const input = action.command.input
  if (control.key === undefined) return { type: action.command.type, input: ordered }
  if (input === null || typeof input !== 'object' || Array.isArray(input)) return undefined
  return { type: action.command.type, input: { ...input, [control.key]: ordered } }
}

/** The chosen values encoded in an exact command produced by `selectionCommand`. */
export function selectionValues(action: ActionOption, command: GameCommand): readonly string[] | undefined {
  const control = setControl(action)
  if (!control || command.type !== action.command.type) return undefined
  const input = command.input
  const raw = control.key === undefined ? input
    : input !== null && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>)[control.key] : undefined
  if (!Array.isArray(raw) || !raw.every(value => typeof value === 'string')) return undefined
  return raw as readonly string[]
}

/**
 * Resolve a submitted command to the legal action it instantiates. Ordinary actions must match
 * exactly; set-controlled actions accept any subset that `selectionCommand` produces.
 */
export function matchCommand(actions: readonly ActionOption[], command: GameCommand): ActionOption | undefined {
  const key = canonicalJson(command)
  for (const action of actions) {
    const control = setControl(action)
    if (!control) { if (canonicalJson(action.command) === key) return action; continue }
    const values = selectionValues(action, command)
    if (values === undefined) continue
    const exact = selectionCommand(action, values)
    if (exact && canonicalJson(exact) === key) return action
  }
  return undefined
}

/** Turn a player's or bot's proposal into an exact legal command, or undefined. */
export function resolveAction(actions: readonly ActionOption[], request: { readonly actionId: string; readonly values?: readonly string[] }): GameCommand | undefined {
  const action = actions.find(candidate => candidate.id === request.actionId)
  if (!action) return undefined
  if (setControl(action)) return selectionCommand(action, request.values ?? [])
  return request.values === undefined ? action.command : undefined
}
